<template>
  <component
    :is="computedTag"
    :type="nativeType"
    :href="href"
    :to="to"
    :class="computedClasses"
    :disabled="computedDisabled"
    @click="handleClick"
  >
    <!-- Loading spinner (Bootstrap 4 compatible) -->
    <span
      v-if="loading"
      class="spinner-border spinner-border-sm mr-2"
      role="status"
      aria-hidden="true"
    >
    </span>
    <span v-if="loading" class="sr-only">Loading...</span>

    <slot />
  </component>
</template>

<script>
export default {
  name: 'BButton',
  emits: ['click'],
  props: {
    // Bootstrap Vue BButton API compatibility
    variant: {
      type: String,
      default: 'primary',
      validator: val => [
        'primary', 'secondary', 'success', 'danger', 'warning',
        'info', 'light', 'dark', 'link', 'outline-primary',
        'outline-secondary', 'outline-success', 'outline-danger',
        'outline-warning', 'outline-info', 'outline-light',
        'outline-dark', 'themed'
      ].includes(val)
    },
    size: {
      type: String,
      validator: val => ['sm', 'lg'].includes(val)
    },
    block: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'button'
    },
    href: {
      type: String,
      default: null
    },
    to: {
      type: [String, Object],
      default: null
    },
    tag: {
      type: String,
      default: null
    },

    // Custom enhancements
    loading: {
      type: Boolean,
      default: false
    },
    pill: {
      type: Boolean,
      default: false
    },
    squared: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    computedTag() {
      if (this.tag) return this.tag
      if (this.href) return 'a'
      if (this.to) return 'router-link'
      return 'button'
    },
    nativeType() {
      if (this.computedTag === 'button') {
        return this.type
      }
      return undefined
    },
    computedDisabled() {
      return this.disabled || this.loading
    },
    computedClasses() {
      const classes = ['btn']

      // Handle variant (including outline variants and themed)
      // if (this.variant === 'themed') {
      //   // For themed variant, don't add btn-themed class automatically
      //   // The themed styling will come from custom CSS classes passed via class attribute
      //   // This maintains compatibility with existing themed button styles
      // } else if (this.variant.startsWith('outline-')) {
      //   classes.push(`btn-${this.variant}`)
      // } else {
      //   classes.push(`btn-${this.variant}`)
      // }
      classes.push(`btn-${this.variant}`)

      // Size classes
      if (this.size) {
        classes.push(`btn-${this.size}`)
      }

      // Block button (Bootstrap 4)
      if (this.block) {
        classes.push('btn-block')
      }

      // Custom shape classes
      if (this.pill) {
        classes.push('rounded-pill')
      }
      if (this.squared) {
        classes.push('rounded-0')
      }

      // Loading state
      if (this.loading) {
        classes.push('disabled')
      }

      return classes
    }
  },
  methods: {
    handleClick(event) {
      if (!this.computedDisabled) {
        this.$emit('click', event)
      } else {
        event.preventDefault()
        event.stopPropagation()
      }
    }
  }
}
</script>

<style scoped>
.btn.disabled {
  pointer-events: none;
  opacity: 0.6;
}

.btn .spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Future Bootstrap 5 compatibility classes */
.btn-block {
  display: block;
  width: 100%;
}

/* Custom pill and squared variants */
.rounded-pill {
  border-radius: 50rem !important;
}

.rounded-0 {
  border-radius: 0 !important;
}
</style>
